import { useRef, useState, useEffect, useCallback } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// ── Hooks ──────────────────────────────────────────────────────────────────────

// Simulates position:sticky via translateY — works even when ancestors have overflow:hidden
function useStickyY(sectionRef, topOffsetPx = 60) {
  const [ty, setTy] = useState(0);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const onScroll = () => {
      const rect    = el.getBoundingClientRect();
      const sectionH = el.offsetHeight;
      const viewH    = window.innerHeight;
      // panel height is viewport minus top offset and some bottom breathing room
      const panelH   = viewH - topOffsetPx - 48;
      const maxTY    = Math.max(0, sectionH - panelH - topOffsetPx);
      const raw      = Math.max(0, -rect.top + topOffsetPx);
      setTy(Math.min(raw, maxTY));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []); // eslint-disable-line
  return ty;
}

// ── Data ───────────────────────────────────────────────────────────────────────

const CHAPTERS = [
  {
    number: "01",
    title: "AI-Powered Signals",
    subtitle: "Real-time intelligence, always on",
    description:
      "Our AI processes thousands of data points every second — price action, volume, sentiment, and macro signals — surfacing high-conviction opportunities before they become obvious to the market.",
    color: "#10B77F",
    glowColor: "rgba(16,183,127,0.12)",
    highlight: "signals",
    tags: ["Pattern recognition", "Live sentiment", "Risk scoring"],
  },
  {
    number: "02",
    title: "Portfolio Analytics",
    subtitle: "Deep visibility into every position",
    description:
      "Understand your exposure at a glance. Sector allocation, correlation matrices, and risk-adjusted attribution — designed for investors who take their portfolios seriously.",
    color: "#60A5FA",
    glowColor: "rgba(96,165,250,0.12)",
    highlight: "holdings",
    tags: ["Allocation heatmap", "Correlation analysis", "Performance attribution"],
  },
  {
    number: "03",
    title: "Market Intelligence",
    subtitle: "Stay ahead of what moves markets",
    description:
      "From macro trends to earnings surprises, Alpha Stack tracks the signals that matter — delivered in a format built for fast, confident decision-making.",
    color: "#A78BFA",
    glowColor: "rgba(167,139,250,0.12)",
    highlight: "sparkline",
    tags: ["Macro calendar", "Earnings synthesis", "News analysis"],
  },
];

const HOLDINGS = [
  { ticker: "AAPL", pct: 42, color: "#60A5FA" },
  { ticker: "NVDA", pct: 28, color: "#10B77F" },
  { ticker: "MSFT", pct: 18, color: "#A78BFA" },
  { ticker: "ETFs", pct: 12, color: "#FBBF24" },
];

const AI_SIGNALS = [
  { ticker: "AAPL", action: "BUY",  target: "+2.4%", color: "#10B77F" },
  { ticker: "NVDA", action: "BUY",  target: "+5.2%", color: "#10B77F" },
  { ticker: "TSLA", action: "HOLD", target: "+0.8%", color: "#FBBF24" },
];

// ── Dashboard ─────────────────────────────────────────────────────────────────

function DashboardShowcase({ highlight, accentColor }) {
  const hlSignals  = highlight === "signals";
  const hlHoldings = highlight === "holdings";
  const hlSpark    = highlight === "sparkline";
  const glow = (on) => on ? `0 0 0 1px ${accentColor}44, 0 4px 20px ${accentColor}18` : "none";

  return (
    <div style={{
      background: "#080A12",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 18,
      overflow: "hidden",
      boxShadow:
        "0 60px 140px rgba(0,0,0,0.85)," +
        "0 0 0 1px rgba(255,255,255,0.04)," +
        "inset 0 1px 0 rgba(255,255,255,0.06)",
    }}>
      {/* Chrome */}
      <div style={{
        padding: "11px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)",
        display: "flex", alignItems: "center", gap: 10,
        background: "rgba(0,0,0,0.3)",
      }}>
        <div style={{ display: "flex", gap: 6 }}>
          {["#FF5F57","#FEBC2E","#28C840"].map(c => (
            <div key={c} style={{ width: 10, height: 10, borderRadius: "50%", background: c, opacity: 0.75 }} />
          ))}
        </div>
        <span style={{
          flex: 1, textAlign: "center",
          fontSize: "0.69rem", color: "rgba(255,255,255,0.22)",
          fontFamily: "Space Grotesk, sans-serif",
        }}>AlphaStack — Portfolio Overview</span>
        <span style={{
          fontSize: "0.58rem", fontWeight: 700, padding: "2px 7px", borderRadius: 4,
          background: "rgba(16,183,127,0.1)", border: "1px solid rgba(16,183,127,0.2)",
          color: "#10B77F", fontFamily: "Space Grotesk, sans-serif", letterSpacing: "0.05em",
        }}>BETA</span>
      </div>

      <div style={{ padding: "16px 18px" }}>
        {/* Value */}
        <div style={{ marginBottom: 14 }}>
          <div style={{
            fontSize: "0.58rem", color: "rgba(255,255,255,0.25)", fontFamily: "Space Grotesk, sans-serif",
            textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 5,
          }}>Portfolio Value</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{
              fontSize: "1.65rem", fontWeight: 800, letterSpacing: "-0.03em",
              color: "#F0EEF6", fontFamily: "Space Grotesk, sans-serif", lineHeight: 1,
            }}>$84,291</span>
            <span style={{
              fontSize: "0.76rem", fontWeight: 700, color: "#10B77F",
              background: "rgba(16,183,127,0.1)", border: "1px solid rgba(16,183,127,0.18)",
              padding: "3px 9px", borderRadius: 7, fontFamily: "Space Grotesk, sans-serif",
            }}>↑ +18.4%</span>
          </div>
        </div>

        {/* Sparkline */}
        <div style={{
          background: hlSpark ? `${accentColor}08` : "rgba(255,255,255,0.018)",
          border: hlSpark ? `1px solid ${accentColor}44` : "1px solid rgba(255,255,255,0.05)",
          borderRadius: 11, padding: "9px 12px", marginBottom: 14, height: 76, overflow: "hidden",
          transition: "border-color 0.55s ease, background 0.55s ease, box-shadow 0.55s ease",
          boxShadow: glow(hlSpark),
        }}>
          <div style={{
            fontSize: "0.57rem", color: "rgba(255,255,255,0.24)",
            fontFamily: "Space Grotesk, sans-serif", marginBottom: 4,
          }}>30-Day Performance</div>
          <svg width="100%" height="40" viewBox="0 0 320 40" preserveAspectRatio="none">
            <defs>
              <linearGradient id="scSpkG" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={hlSpark ? accentColor : "#10B77F"} stopOpacity="0.3" />
                <stop offset="100%" stopColor={hlSpark ? accentColor : "#10B77F"} stopOpacity="0"   />
              </linearGradient>
            </defs>
            <path d="M0,40 L0,34 C18,28 35,32 55,25 C75,18 92,22 112,15 C132,9 150,13 170,7 C190,2 210,5 230,2 C250,0 270,1 295,0 L320,0 L320,40 Z" fill="url(#scSpkG)" />
            <path d="M0,34 C18,28 35,32 55,25 C75,18 92,22 112,15 C132,9 150,13 170,7 C190,2 210,5 230,2 C250,0 270,1 295,0 L320,0"
              fill="none" stroke={hlSpark ? accentColor : "#10B77F"} strokeWidth="1.8" strokeLinecap="round" />
            <circle cx="320" cy="0" r="3.5" fill={hlSpark ? accentColor : "#10B77F"} />
            <circle cx="320" cy="0" r="7"   fill={hlSpark ? accentColor : "#10B77F"} fillOpacity="0.2" />
          </svg>
        </div>

        {/* Holdings */}
        <div style={{
          marginBottom: 12,
          background: hlHoldings ? `${accentColor}06` : "transparent",
          border: hlHoldings ? `1px solid ${accentColor}33` : "1px solid transparent",
          borderRadius: 10, padding: hlHoldings ? "9px 10px" : "0",
          transition: "all 0.55s ease",
          boxShadow: glow(hlHoldings),
        }}>
          <div style={{
            fontSize: "0.57rem", color: "rgba(255,255,255,0.24)", fontFamily: "Space Grotesk, sans-serif",
            textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 8,
          }}>Holdings</div>
          {HOLDINGS.map(({ ticker, pct, color }) => (
            <div key={ticker} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6 }}>
              <span style={{ width: 36, fontSize: "0.7rem", fontWeight: 700, color: "#F0EEF6", fontFamily: "Space Grotesk, sans-serif" }}>{ticker}</span>
              <div style={{ flex: 1, height: 3, borderRadius: 99, background: "rgba(255,255,255,0.05)", overflow: "hidden" }}>
                <div style={{
                  height: "100%", width: `${pct}%`, borderRadius: 99,
                  background: hlHoldings ? accentColor : color,
                  boxShadow: hlHoldings ? `0 0 8px ${accentColor}60` : `0 0 8px ${color}60`,
                  transition: "background 0.55s ease, box-shadow 0.55s ease",
                }} />
              </div>
              <span style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.36)", fontFamily: "Space Grotesk, sans-serif", width: 30, textAlign: "right" }}>{pct}%</span>
            </div>
          ))}
        </div>

        {/* AI Signals */}
        <div style={{
          background: hlSignals ? `${accentColor}07` : "transparent",
          border: hlSignals ? `1px solid ${accentColor}40` : "1px solid transparent",
          borderRadius: 10, padding: hlSignals ? "9px 10px" : "0",
          transition: "all 0.55s ease",
          boxShadow: glow(hlSignals),
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 5, marginBottom: 7,
            fontSize: "0.57rem", color: "rgba(255,255,255,0.24)",
            fontFamily: "Space Grotesk, sans-serif", textTransform: "uppercase", letterSpacing: "0.12em",
          }}>
            <span style={{
              width: 5, height: 5, borderRadius: "50%",
              background: hlSignals ? accentColor : "#10B77F",
              animation: "pulseGold 1.8s ease-in-out infinite",
              transition: "background 0.55s ease",
            }} />
            AI Signals · Live
          </div>
          {AI_SIGNALS.map(({ ticker, action, target, color }, i) => (
            <div key={ticker} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "5px 0",
              borderBottom: i < AI_SIGNALS.length - 1 ? "1px solid rgba(255,255,255,0.032)" : "none",
            }}>
              <span style={{ width: 38, fontSize: "0.71rem", fontWeight: 700, color: "#F0EEF6", fontFamily: "Space Grotesk, sans-serif" }}>{ticker}</span>
              <span style={{
                fontSize: "0.58rem", fontWeight: 700, letterSpacing: "0.05em",
                padding: "2px 7px", borderRadius: 999,
                background: (hlSignals ? accentColor : color) + "18",
                border: `1px solid ${(hlSignals ? accentColor : color)}32`,
                color: hlSignals ? accentColor : color,
                transition: "all 0.55s ease",
              }}>{action}</span>
              <span style={{
                fontSize: "0.71rem", fontWeight: 700,
                color: hlSignals ? accentColor : "#10B77F",
                fontFamily: "Space Grotesk, sans-serif",
                transition: "color 0.55s ease",
              }}>{target}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Floating card ─────────────────────────────────────────────────────────────

function FloatingCard({ icon, label, value, valueColor, rotation, delay = "0s", style: pos }) {
  return (
    <div style={{ position: "absolute", ...pos, zIndex: 10, animation: `floatCard 5s ease-in-out ${delay} infinite`, "--fc-r": rotation }}>
      <div style={{
        background: "rgba(8,10,18,0.92)", border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 14, padding: "10px 16px",
        backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
        boxShadow: "0 20px 50px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)",
        display: "flex", alignItems: "center", gap: 10, whiteSpace: "nowrap",
      }}>
        <div style={{
          width: 34, height: 34, borderRadius: 10,
          background: `${valueColor}12`, border: `1px solid ${valueColor}22`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "1rem", flexShrink: 0,
        }}>{icon}</div>
        <div>
          <div style={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.3)", fontFamily: "Space Grotesk, sans-serif", marginBottom: 2 }}>{label}</div>
          <div style={{ fontSize: "0.9rem", fontWeight: 800, color: valueColor, fontFamily: "Space Grotesk, sans-serif" }}>{value}</div>
        </div>
      </div>
    </div>
  );
}

// ── Chapter text block ────────────────────────────────────────────────────────

function ChapterBlock({ chapter, active, onEnter }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) onEnter(); },
      { threshold: 0.42, rootMargin: "-5% 0px -5% 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [onEnter]);

  return (
    <div ref={ref} style={{ minHeight: "88vh", display: "flex", alignItems: "center", padding: "56px 0" }}>
      <div style={{
        opacity: active ? 1 : 0.22,
        transform: `translateX(${active ? 0 : -14}px)`,
        transition: "opacity 0.55s ease, transform 0.55s ease",
      }}>
        <div style={{
          fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.15em",
          color: chapter.color, fontFamily: "Space Grotesk, sans-serif",
          marginBottom: 16, opacity: 0.9,
        }}>{chapter.number}</div>

        <h2 style={{
          fontFamily: "Space Grotesk, sans-serif",
          fontSize: "clamp(1.9rem, 3vw, 2.7rem)",
          fontWeight: 800, lineHeight: 1.1, letterSpacing: "-0.03em",
          color: "#F0EEF6", marginBottom: 12,
        }}>{chapter.title}</h2>

        <div style={{
          fontSize: "0.93rem", fontWeight: 600, color: chapter.color,
          marginBottom: 20, fontFamily: "Space Grotesk, sans-serif",
        }}>{chapter.subtitle}</div>

        <p style={{
          fontSize: "0.98rem", color: "rgba(240,238,246,0.45)",
          lineHeight: 1.78, marginBottom: 28,
          fontFamily: "Space Grotesk, sans-serif", maxWidth: 420,
        }}>{chapter.description}</p>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {chapter.tags.map(tag => (
            <span key={tag} style={{
              fontSize: "0.74rem", fontWeight: 600,
              padding: "5px 13px", borderRadius: 999,
              background: `${chapter.color}0E`,
              border: `1px solid ${chapter.color}28`,
              color: chapter.color,
              fontFamily: "Space Grotesk, sans-serif",
            }}>{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────

export default function ShowcaseSection() {
  const sectionRef    = useRef(null);
  const dashboardRef  = useRef(null);
  const [activeChapter, setActiveChapter] = useState(0);

  const stickyY = useStickyY(sectionRef, 60);
  const chapter = CHAPTERS[activeChapter];

  // GSAP ScrollTrigger: 3D rotation driven by scroll
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        dashboardRef.current,
        { rotateY: -14, rotateX: 4 },
        {
          rotateY: 14,
          rotateX: -4,
          ease: "sine.inOut",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top center",
            end: "bottom center",
            scrub: 1.5,
            onUpdate: (self) => {
              const ch = self.progress < 0.38 ? 0 : self.progress < 0.72 ? 1 : 2;
              setActiveChapter(ch);
            },
          },
        }
      );
    });
    return () => ctx.revert();
  }, []);

  const makeEnter = useCallback((i) => () => setActiveChapter(i), []);

  return (
    <section
      ref={sectionRef}
      id="showcase"
      style={{ position: "relative", padding: "40px 24px 0" }}
    >
      {/* Ambient glow shifts with chapter */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: `radial-gradient(ellipse 55% 60% at 72% 45%, ${chapter.glowColor}, transparent 70%)`,
        transition: "background 0.9s ease",
      }} />

      <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>

        {/* Header row */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 8 }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            fontSize: "0.67rem", fontWeight: 700, letterSpacing: "0.14em",
            textTransform: "uppercase", color: "rgba(255,255,255,0.28)",
            fontFamily: "Space Grotesk, sans-serif",
          }}>
            <span style={{ width: 18, height: 1, background: "rgba(255,255,255,0.2)" }} />
            Platform Capabilities
          </div>

          {/* Chapter pill progress */}
          <div style={{ display: "flex", gap: 5 }}>
            {CHAPTERS.map((_, i) => (
              <button key={i} onClick={() => setActiveChapter(i)} style={{
                height: 6, borderRadius: 3, border: "none", cursor: "pointer", padding: 0,
                width: i === activeChapter ? 22 : 6,
                background: i === activeChapter ? chapter.color : "rgba(255,255,255,0.14)",
                boxShadow: i === activeChapter ? `0 0 10px ${chapter.color}90` : "none",
                transition: "all 0.4s ease",
              }} />
            ))}
          </div>
        </div>

        {/* Two-column layout */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1.05fr",
          gap: "clamp(32px, 5vw, 64px)",
          alignItems: "start",
        }} className="showcase-grid">

          {/* ── Left: 3 scrolling chapter blocks ──── */}
          <div>
            {CHAPTERS.map((ch, i) => (
              <ChapterBlock
                key={i}
                chapter={ch}
                active={i === activeChapter}
                onEnter={makeEnter(i)}
              />
            ))}
          </div>

          {/* ── Right: JS-sticky 3D dashboard ──────── */}
          <div style={{ position: "relative" }}>
            {/*
              translateY simulates sticky — works even with overflow:hidden on ancestors.
              The 3D rotation (ry/rx) is driven by scroll progress across the whole section.
            */}
            <div style={{
              transform: `translateY(${stickyY}px)`,
              willChange: "transform",
            }}>
              <div style={{
                perspective: "1400px",
                perspectiveOrigin: "center center",
                position: "relative",
                paddingTop: 16,
              }}>
                {/* Rotating dashboard — GSAP owns the transform */}
                <div ref={dashboardRef} style={{ transformStyle: "preserve-3d" }}>
                  <DashboardShowcase highlight={chapter.highlight} accentColor={chapter.color} />
                </div>

                {/* Floating accent cards */}
                <FloatingCard
                  icon="📈" label="Monthly Return" value="+18.4%"
                  valueColor="#10B77F" rotation="-4deg" delay="0s"
                  style={{ top: -18, right: -20 }}
                />
                <FloatingCard
                  icon="⚡" label="AI Signals Active" value="94 Live"
                  valueColor="#60A5FA" rotation="3deg" delay="1.8s"
                  style={{ bottom: 32, left: -22 }}
                />

                {/* Glow beneath */}
                <div style={{
                  position: "absolute", bottom: -50, left: "50%",
                  transform: "translateX(-50%)",
                  width: "70%", height: 70, pointerEvents: "none",
                  background: `radial-gradient(ellipse, ${chapter.color}2E 0%, transparent 70%)`,
                  filter: "blur(24px)",
                  transition: "background 0.9s ease",
                }} />
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
